import {
  type TransitionEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

// Fallback if transitionend doesn't fire (e.g. prefers-reduced-motion,
// interrupted transitions). Sized to cover the longest expected leave
// transition (currently ~300ms modal / ~200ms toast) plus a small buffer.
// Shared by modals/sheets and toasts — see `ToastContainer` for the
// non-modal consumer.
export const SAFETY_TIMEOUT = 400;

// Test-environment escape hatch. When true, the hook skips the closing
// animation and unmounts synchronously. Mirrors react-spring's
// `Globals.assign({ skipAnimation: true })` pattern so consumer tests
// can assert instant dismiss without fake timers.
let skipTransitionForTests = false;

export function setSkipModalTransitionForTests(skip: boolean) {
  skipTransitionForTests = skip;
}

export type ModalTransitionResult = { closing: boolean };

interface UseModalTransitionOptions {
  isOpen: boolean;
  onRest?: (result: ModalTransitionResult) => void;
  // Per-instance opt-out from the global test-skip flag. Callers whose tests
  // need the closing animation to play out (e.g. toasts that assert on the
  // leave window) should pass `false`. Omit to inherit the global flag.
  skipTransition?: boolean;
}

export function useModalTransition({
  isOpen,
  onRest,
  skipTransition,
}: UseModalTransitionOptions) {
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [closing, setClosing] = useState(false);
  const closingRef = useRef(false);
  const safetyTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const prevIsOpenRef = useRef(isOpen);
  const onRestRef = useRef(onRest);
  onRestRef.current = onRest;

  const finishClose = useCallback(() => {
    clearTimeout(safetyTimeoutRef.current);
    setShouldRender(false);
    setClosing(false);
    closingRef.current = false;
    onRestRef.current?.({ closing: true });
  }, []);

  // State machine: adjust state during render when isOpen changes.
  //   open  → shouldRender=true,  closing=false  (@starting-style fires)
  //   close → shouldRender=true,  closing=true   (.closing class applied)
  //   done  → shouldRender=false, closing=false   (transitionend or timeout)
  if (isOpen !== prevIsOpenRef.current) {
    prevIsOpenRef.current = isOpen;
    if (isOpen) {
      clearTimeout(safetyTimeoutRef.current);
      closingRef.current = false;

      if (closing) {
        setClosing(false);
      }

      if (!shouldRender) {
        setShouldRender(true);
      }
    } else if (shouldRender) {
      const effectiveSkip = skipTransition ?? skipTransitionForTests;
      if (effectiveSkip) {
        // Skip the closing animation entirely in tests.
        setShouldRender(false);
        onRestRef.current?.({ closing: true });
      } else {
        closingRef.current = true;

        if (!closing) {
          setClosing(true);
        }
      }
    }
  }

  useEffect(() => {
    if (closing) {
      safetyTimeoutRef.current = setTimeout(finishClose, SAFETY_TIMEOUT);
    }
    return () => clearTimeout(safetyTimeoutRef.current);
  }, [closing, finishClose]);

  const handleTransitionEnd = useCallback(
    (e: TransitionEvent<HTMLDivElement>) => {
      if (
        e.propertyName === 'opacity' &&
        e.target === e.currentTarget &&
        closingRef.current
      ) {
        finishClose();
      }
    },
    [finishClose]
  );

  return { shouldRender, closing, handleTransitionEnd };
}
